/**
 * "Money out" — `mockups/studio/money.html` §money out, and the two folds under it.
 *
 * `MoneyOutView.lines` is every expense row this month. The mockup's first line folds
 * the eight subscriptions into one — "Eight vendors · Software and tooling · 8 card
 * charges · $679.00" — and `MoneyOutSchema.subscriptions` is exactly that fold, with
 * its own rows and its own total, so the page prints the summary line and never adds
 * anything up: `subscriptions.total` is the API's sum, not this file's.
 *
 * `basis` is printed **verbatim**. It carries "34 h × $30", "$5 cap, plus returns",
 * "0.55% of $3,260.00" — the mockup's own third column, written by whoever recorded
 * the expense. A page that recomposed it from `hours` and `rate` would print a
 * different sentence for the rail fees, which have neither.
 */

import { sentenceCase, type ExpenseView, type MoneyOutView } from '@fareast/shared'

import { railLabel } from '../../../components/client/index.js'
import { Card, CardBody } from '../../../components/portal/Card.js'
import { Fold } from '../../../components/portal/Disclosure.js'
import { ScrollX, Section, SectionHead } from '../../../components/portal/Layout.js'
import { Tag } from '../../../components/portal/Status.js'
import { TBody, THead, Table, Td, Th, Tr } from '../../../components/portal/Table.js'
import { Answer, B, DayText, Muted, Num, Prose, Small } from '../../../components/portal/Text.js'
import { useFormat } from '../../../format/useFormat.js'
import { EXPENSE_KIND_WORDS } from './state.js'

export type MoneyOutSectionProps = {
  readonly out: MoneyOutView
}

export function MoneyOutSection({ out }: MoneyOutSectionProps) {
  const format = useFormat()

  /* The mockup's main table: everything that is not a subscription, plus one folded
     line standing for the eight that are. */
  const standalone = out.lines.filter((line) => line.kind !== 'subscription')
  const subscriptions = out.subscriptions

  return (
    <Section id="out">
      <SectionHead
        title={`Money out — ${format.monthLong(out.period)}`}
        note={`Every payment that left the account · ${String(standalone.length + (subscriptions.rows.length === 0 ? 0 : 1))} lines · ${kindWords(out)}`}
      />

      <Answer>
        Delivering the clients and running the studio cost{' '}
        <B>
          <Num value={out.delivery_and_running} />
        </B>{' '}
        in {format.monthLong(out.period)}. The draw takes what actually left the account to{' '}
        <B>
          <Num value={out.total} />
        </B>
        .
      </Answer>

      <Card>
        <CardBody tight scrollX>
          <Table>
            <THead>
              <Tr>
                <Th scope="col">Paid</Th>
                <Th scope="col">Payee</Th>
                <Th scope="col">For</Th>
                <Th scope="col">Basis</Th>
                <Th scope="col" right>
                  Amount
                </Th>
                <Th scope="col">Rail</Th>
                <Th scope="col">State</Th>
              </Tr>
            </THead>
            <TBody>
              {subscriptions.rows.length === 0 ? null : (
                <Tr>
                  <Td date>
                    <SubscriptionSpan rows={subscriptions.rows} />
                  </Td>
                  <Td>
                    <B>{`${String(subscriptions.rows.length)} vendors`}</B>
                  </Td>
                  <Td>{SUBSCRIPTION_CATEGORY}</Td>
                  <Td>
                    <Small>
                      <Muted>{`${String(subscriptions.rows.length)} charges`}</Muted>
                    </Small>
                  </Td>
                  <Td right num>
                    <B>
                      <Num value={subscriptions.total} />
                    </B>
                  </Td>
                  <Td>{foldedRail(subscriptions.rows)}</Td>
                  <Td>
                    <Small>
                      <Muted>{foldedStatus(subscriptions.rows)}</Muted>
                    </Small>
                  </Td>
                </Tr>
              )}

              {standalone.map((line) => {
                const rail = railLabel(line.rail)
                return (
                  <Tr key={line.id}>
                    <Td date>
                      <DayText value={line.paid_on} />
                    </Td>
                    <Td>
                      <B>{line.payee}</B>
                    </Td>
                    <Td>{line.purpose}</Td>
                    <Td>
                      <Small>
                        <Muted>{line.basis === '' ? '—' : line.basis}</Muted>
                      </Small>
                    </Td>
                    <Td right num>
                      <B>
                        <Num value={line.amount} />
                      </B>
                    </Td>
                    <Td>{rail === null ? null : <Tag>{rail}</Tag>}</Td>
                    <Td>
                      <Small>
                        <Muted>{sentenceCase(line.status)}</Muted>
                      </Small>
                    </Td>
                  </Tr>
                )
              })}

              <Tr variant="total">
                <Td colSpan={4}>{`Money out, ${format.monthLong(out.period)}`}</Td>
                <Td right num>
                  <Num value={out.total} lg />
                </Td>
                <Td />
                <Td />
              </Tr>
            </TBody>
          </Table>
        </CardBody>
      </Card>

      {subscriptions.rows.length === 0 ? null : (
        <Fold
          summary={`The ${String(subscriptions.rows.length)} subscriptions behind that first line`}
        >
          <ScrollX>
            <Table>
              <THead>
                <Tr>
                  <Th scope="col">Vendor</Th>
                  <Th scope="col">What it is for</Th>
                  <Th scope="col">Charged</Th>
                  <Th scope="col" right>
                    Amount
                  </Th>
                </Tr>
              </THead>
              <TBody>
                {subscriptions.rows.map((line) => (
                  <Tr key={line.id}>
                    <Td>
                      <B>{line.payee}</B>
                    </Td>
                    <Td>{line.purpose}</Td>
                    <Td date>
                      <DayText value={line.paid_on} />
                    </Td>
                    <Td right num>
                      <Num value={line.amount} />
                    </Td>
                  </Tr>
                ))}
                <Tr variant="total">
                  <Td colSpan={3}>{`${String(subscriptions.rows.length)} subscriptions`}</Td>
                  <Td right num>
                    <Num value={subscriptions.total} />
                  </Td>
                </Tr>
              </TBody>
            </Table>
          </ScrollX>
        </Fold>
      )}

      <Fold summary="How this ledger is built, and what is not in it">
        <Prose>
          Contractors invoice against the hours they logged in the month and are paid on the last
          working day of it, so a payout line carries its own hours and rate in the basis column and
          nothing here recomputes them. The owner draw is fixed and is not hours.
        </Prose>
        <Prose>
          Stripe and Wise fees never leave as a payment — they are netted off at source. They sit in
          the ledger because the collected figure above is gross invoice value, so both halves
          reconcile to the invoice ledger instead of one of them quietly absorbing the rails.
        </Prose>
        <Prose>
          <b>Ad spend is not here, and never will be.</b> Clients pay the platforms directly; it is
          neither our revenue nor our cost. Odoo licences and Odoo.sh hosting are the same — billed
          by Odoo to the client, pass-through, not ours. A ledger that showed either would make the
          studio look four times its size and the margin look a third of what it is.
        </Prose>
      </Fold>
    </Section>
  )
}

/** "1–15 Aug" — the first and last day a subscription was charged, as one cell. */
function SubscriptionSpan({ rows }: { readonly rows: MoneyOutView['subscriptions']['rows'] }) {
  const days = rows.map((row) => row.paid_on).sort()
  const first = days[0]
  const last = days[days.length - 1]
  if (first === undefined || last === undefined) return null
  if (first === last) return <DayText value={first} />
  return (
    <>
      <DayText value={first} />
      {' – '}
      <DayText value={last} />
    </>
  )
}

/** "contractors, tooling, rails, draw" — the kinds present, in the order they were summed. */
export function kindWords(out: MoneyOutView): string {
  const present = out.by_kind.map((one) => EXPENSE_KIND_WORDS[one.kind].short)
  return present.length === 0 ? 'nothing recorded' : present.join(', ')
}

/* ------------------------------------------------------ what the folded line says */

/**
 * The one value every folded row shares, or null when they do not share one.
 *
 * The fold stands for eight rows and prints one line, so each of its three
 * descriptive cells is either a fact about all of them or a count. It said
 * "Software and tooling", `<Tag>Card</Tag>` and "Paid" as literals, which is what
 * this month's eight subscriptions happen to be — a ninth on SEPA, or one still
 * pending, and the line would have said something untrue about money that left the
 * account.
 */
function shared<T>(rows: readonly ExpenseView[], of: (row: ExpenseView) => T): T | null {
  const first = rows[0]
  if (first === undefined) return null
  const value = of(first)
  return rows.every((row) => of(row) === value) ? value : null
}

/**
 * What the folded line is **for** — the kind's own word, not the rows'.
 *
 * The eight rows have eight purposes ("Mail, drive, calendar · 4 seats", "SEO suite
 * — audits, ranks, backlinks · 3 seats"), and the fold has one cell. So this column
 * is a category, which is what `mockups/studio/money.html` prints there and what
 * every row in the fold genuinely shares: they are all `kind: 'subscription'`, and
 * the fold exists because of that and nothing else.
 *
 * It is a constant for the same reason `kindSentence`'s nouns are: `ExpenseKind` is a
 * closed set and the word for one of them is the studio's, not a column's.
 */
const SUBSCRIPTION_CATEGORY = 'Software and tooling'

/** Their common rail as a tag, or the word for several. */
export function foldedRail(rows: readonly ExpenseView[]) {
  const one = shared(rows, (row) => row.rail)
  if (one === null) return <Muted>Several</Muted>
  return <Tag>{railLabel(one)}</Tag>
}

/** "Paid" when every one of them is, and what is outstanding when they are not. */
export function foldedStatus(rows: readonly ExpenseView[]): string {
  const one = shared(rows, (row) => row.status)
  if (one !== null) return sentenceCase(one)
  const paid = rows.filter((row) => row.status === 'paid').length
  return `${String(paid)} of ${String(rows.length)} paid`
}
